import { z } from 'zod';
import { terraContract } from '@terra/shared-authorization';
import {
  CloneEvaluationDatasetRequestSchema,
  CloneEvaluationDatasetResponseSchema,
  BulkDeleteEvaluationDatasetsRequestSchema,
  BulkDeleteEvaluationDatasetsResponseSchema,
  DeleteEvalTenantApplicationRequestSchema,
  DeleteEvalTenantApplicationResponseSchema,
  DisableEvalTenantApplicationAutomationResponseSchema,
  DeleteEvaluationDatasetResponseSchema,
  ListEvalTenantApplicationsResponseSchema,
  DeleteEvaluationDatasetTasksRequestSchema,
  DeleteEvaluationDatasetTasksResponseSchema,
  ReorderEvaluationDatasetTasksRequestSchema,
  ReorderEvaluationDatasetTasksResponseSchema,
  ImportEvaluationDatasetRequestSchema,
  ImportEvaluationDatasetResponseSchema,
  EvaluationDatasetExpectationsResponseSchema,
  EvaluationDatasetTypeSchema,
  PatchEvaluationDatasetExpectationsRequestSchema,
  InstantiateEvaluationDatasetRequestSchema,
  InstantiateEvaluationDatasetResponseSchema,
  EnsureEvalTenantDatasetInstantiationRequestSchema,
  EnsureEvalTenantDatasetInstantiationResponseSchema,
  ListEvaluationDatasetInstantiationsQuerySchema,
  ListEvaluationDatasetInstantiationsResponseSchema,
  ListEvaluationDatasetsResponseSchema,
  EvaluationDatasetRootTypeSchema,
  ListEvaluationDatasetRootsResponseSchema,
  SnapshotEvaluationDatasetRequestSchema,
  SnapshotEvaluationDatasetResponseSchema,
  SuggestEvaluationDataRequestSchema,
  SuggestEvaluationDataResponseSchema,
  SuggestEvaluationDatasetExpectationsRequestSchema,
  SuggestEvaluationDatasetExpectationsResponseSchema,
} from './evaluation-dataset.schemas';
import {
  CreateEvaluationDatasetGroupFromFalsePositivesRequestSchema,
  CreateEvaluationDatasetGroupFromFalsePositivesResponseSchema,
  CreateEvaluationDatasetGroupRequestSchema,
  CreateEvaluationDatasetGroupResponseSchema,
  DeleteEvaluationDatasetGroupRequestSchema,
  DeleteEvaluationDatasetGroupResponseSchema,
  GetEvaluationDatasetGroupResponseSchema,
  ListEvaluationDatasetGroupsResponseSchema,
  UpdateEvaluationDatasetGroupMembersRequestSchema,
  UpdateEvaluationDatasetGroupMembersResponseSchema,
} from './evaluation-dataset-group.schemas';
import {
  GenerateEvaluationExperimentGeneratedSummaryResponseSchema,
  GenerateEvaluationExperimentSummaryRequestSchema,
  GetEvaluationExperimentGeneratedSummaryResponseSchema,
} from './evaluation-experiment-summary.schemas';
import {
  EvaluationRunListItemSchema,
  EvaluationRunDetailSchema,
  EvaluationRunIterationsSchema,
  EvaluationRunOutcomeFilterSchema,
  EvaluationRunLogsSchema,
  EvaluationRunArtifactsSchema,
  EvaluationRunArtifactContentSchema,
  RerunEvaluationRunRequestSchema,
  RerunEvaluationRunResponseSchema,
  CancelEvaluationRunResponseSchema,
  DeleteEvaluationRunResponseSchema,
  EvaluationRunStatusSchema,
  UploadEvaluationScenarioRequestSchema,
  UploadEvaluationScenarioResponseSchema,
  ListEvaluationScenariosQuerySchema,
  ListEvaluationScenariosResponseSchema,
  ListEvaluationSignalsQuerySchema,
  ListEvaluationSignalsResponseSchema,
  ListEvaluationComponentsResponseSchema,
  EvaluationComponentTrendsQuerySchema,
  EvaluationComponentTrendsResponseSchema,
  InvokeEvaluationComponentParamsSchema,
  InvokeEvaluationComponentBodySchema,
  InvokeEvaluationComponentResponseSchema,
  GetEvaluationPostAttackPlaybookQuerySchema,
  GetEvaluationPostAttackPlaybookResponseSchema,
  CreateEvaluationExperimentRequestSchema,
  CreateEvaluationExperimentResponseSchema,
  CancelEvaluationExperimentResponseSchema,
  DeleteEvaluationExperimentResponseSchema,
  EvaluationExperimentDetailsSchema,
  ListEvaluationExperimentsResponseSchema,
  RerunEvaluationExperimentRequestSchema,
  SetEvaluationRegressionScheduleRequestSchema,
  SetEvaluationRegressionScheduleResponseSchema,
  RetriggerFailedExperimentRunsRequestSchema,
  RetriggerFailedExperimentRunsResponseSchema,
  ListReusableExperimentConfigsResponseSchema,
  PreviewReusableExperimentConfigsRequestSchema,
  PreviewReusableExperimentConfigsResponseSchema,
  ReuseExperimentConfigsRequestSchema,
  ReuseExperimentConfigsResponseSchema,
  GetEvaluationExperimentSamplesResponseSchema,
  SaveEvaluationConfigPresetRequestSchema,
  SaveEvaluationConfigPresetResponseSchema,
  ListEvaluationConfigPresetsResponseSchema,
  DeleteEvaluationConfigPresetResponseSchema,
  TriggerEvaluationRunRequestSchema,
  TriggerEvaluationRunResponseSchema,
} from './evaluation-run.schemas';

export const cockpitEvaluationRoutes = {
  triggerEvaluationRun: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'POST',
      path: '/evaluation/runs/trigger',
      summary: 'Trigger an evaluation run',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: TriggerEvaluationRunRequestSchema }))
    .output(TriggerEvaluationRunResponseSchema),
  uploadEvaluationScenario: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'POST',
      path: '/evaluation/scenarios/upload',
      summary: 'Upload evaluation scenario JSON',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: UploadEvaluationScenarioRequestSchema }))
    .output(UploadEvaluationScenarioResponseSchema),
  listEvaluationScenarios: terraContract
    .meta({ permissions: ['Evaluation Read'] })
    .route({
      method: 'GET',
      path: '/evaluation/scenarios',
      summary: 'List available evaluation scenarios',
      inputStructure: 'detailed',
    })
    .input(z.object({ query: ListEvaluationScenariosQuerySchema }))
    .output(ListEvaluationScenariosResponseSchema),
  listEvaluationSignals: terraContract
    .meta({ permissions: ['Evaluation Read'] })
    .route({
      method: 'GET',
      path: '/evaluation/signals',
      summary: 'List available signals for evaluation',
      inputStructure: 'detailed',
    })
    .input(z.object({ query: ListEvaluationSignalsQuerySchema }))
    .output(ListEvaluationSignalsResponseSchema),
  listEvaluationRuns: terraContract
    .meta({ permissions: ['Evaluation Read'] })
    .route({
      method: 'GET',
      path: '/evaluation/runs',
      summary: 'List evaluation runs',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        query: z.object({
          applicationId: z.string().optional(),
          status: EvaluationRunStatusSchema.optional(),
          experimentId: z.string().optional(),
          outcomeMatch: EvaluationRunOutcomeFilterSchema.optional(),
          search: z.string().trim().min(1).optional(),
          componentIds: z.string().trim().min(1).optional(),
          limit: z.coerce.number().min(1).max(200).optional().prefault(50),
          offset: z.coerce.number().min(0).optional().prefault(0),
        }),
      }),
    )
    .output(
      z.object({
        runs: z.array(EvaluationRunListItemSchema),
        total: z.number().int().nonnegative(),
      }),
    ),
  getEvaluationRun: terraContract
    .meta({ permissions: ['Evaluation Read'] })
    .route({
      method: 'GET',
      path: '/evaluation/runs/{runId}',
      summary: 'Get evaluation run details',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: z.object({ runId: z.string() }) }))
    .output(EvaluationRunDetailSchema),
  getEvaluationRunIterations: terraContract
    .meta({ permissions: ['Evaluation Read'] })
    .route({
      method: 'GET',
      path: '/evaluation/runs/{runId}/iterations',
      summary: 'Get per-sample iteration outcomes for an evaluation run',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: z.object({ runId: z.string() }) }))
    .output(EvaluationRunIterationsSchema),
  getEvaluationRunLogs: terraContract
    .meta({ permissions: ['Evaluation Read'] })
    .route({
      method: 'GET',
      path: '/evaluation/runs/{runId}/logs',
      summary: 'Get evaluation run logs',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: z.object({ runId: z.string() }),
        query: z.object({
          limit: z.coerce.number().min(1).max(2000).optional().prefault(500),
        }),
      }),
    )
    .output(EvaluationRunLogsSchema),
  getEvaluationRunArtifacts: terraContract
    .meta({ permissions: ['Evaluation Read'] })
    .route({
      method: 'GET',
      path: '/evaluation/runs/{runId}/artifacts',
      summary: 'Get evaluation run artifacts',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: z.object({ runId: z.string() }) }))
    .output(EvaluationRunArtifactsSchema),
  getEvaluationRunArtifactContent: terraContract
    .meta({ permissions: ['Evaluation Read'] })
    .route({
      method: 'GET',
      path: '/evaluation/runs/{runId}/artifacts/{artifactId}/content',
      summary: 'Get evaluation run artifact file content',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: z.object({ runId: z.string(), artifactId: z.string() }),
      }),
    )
    .output(EvaluationRunArtifactContentSchema),
  rerunEvaluationRun: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'POST',
      path: '/evaluation/runs/{runId}/rerun',
      summary: 'Request evaluation run rerun',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: z.object({ runId: z.string() }),
        body: RerunEvaluationRunRequestSchema,
      }),
    )
    .output(RerunEvaluationRunResponseSchema),
  cancelEvaluationRun: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'POST',
      path: '/evaluation/runs/{runId}/cancel',
      summary: 'Cancel a queued or running evaluation run',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: z.object({ runId: z.string() }),
        body: z.object({}).optional(),
      }),
    )
    .output(CancelEvaluationRunResponseSchema),
  deleteEvaluationRun: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'DELETE',
      path: '/evaluation/runs/{runId}',
      summary: 'Delete an evaluation run and its related data',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: z.object({ runId: z.string() }) }))
    .output(DeleteEvaluationRunResponseSchema),
  createEvaluationExperiment: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'POST',
      path: '/evaluation/experiments',
      summary: 'Create and trigger an evaluation experiment',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: CreateEvaluationExperimentRequestSchema }))
    .output(CreateEvaluationExperimentResponseSchema),
  listEvaluationExperiments: terraContract
    .meta({ permissions: ['Evaluation Read'] })
    .route({
      method: 'GET',
      path: '/evaluation/experiments',
      summary: 'List evaluation experiments',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        query: z.object({
          applicationId: z.string().optional(),
          datasetIds: z
            .string()
            .optional()
            .transform((value) => {
              if (!value) {
                return undefined;
              }

              const ids = value
                .split(',')
                .map((id) => id.trim())
                .filter((id) => id.length > 0);

              return ids.length > 0 ? [...new Set(ids)] : undefined;
            }),
          limit: z.coerce.number().min(1).max(200).optional().prefault(50),
        }),
      }),
    )
    .output(ListEvaluationExperimentsResponseSchema),
  getEvaluationExperiment: terraContract
    .meta({ permissions: ['Evaluation Read'] })
    .route({
      method: 'GET',
      path: '/evaluation/experiments/{experimentId}',
      summary: 'Get evaluation experiment details with aggregated metrics',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: z.object({ experimentId: z.string() }) }))
    .output(EvaluationExperimentDetailsSchema),
  getEvaluationExperimentSamples: terraContract
    .meta({ permissions: ['Evaluation Read'] })
    .route({
      method: 'GET',
      path: '/evaluation/experiments/{experimentId}/samples',
      summary:
        'Get per-signal pass/fail results across experiment configs, including every repeat run',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: z.object({ experimentId: z.string() }) }))
    .output(GetEvaluationExperimentSamplesResponseSchema),
  getEvaluationExperimentSummary: terraContract
    .meta({ permissions: ['Evaluation Read'] })
    .route({
      method: 'GET',
      path: '/evaluation/experiments/{experimentId}/summary',
      summary: 'Get a cached generated experiment summary',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: z.object({ experimentId: z.string() }) }))
    .output(GetEvaluationExperimentGeneratedSummaryResponseSchema),
  generateEvaluationExperimentSummary: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'POST',
      path: '/evaluation/experiments/{experimentId}/summary',
      summary: 'Generate or refresh an experiment summary',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: z.object({ experimentId: z.string() }),
        body: GenerateEvaluationExperimentSummaryRequestSchema.optional(),
      }),
    )
    .output(GenerateEvaluationExperimentGeneratedSummaryResponseSchema),
  deleteEvaluationExperiment: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'DELETE',
      path: '/evaluation/experiments/{experimentId}',
      summary: 'Delete an evaluation experiment and its runs',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: z.object({ experimentId: z.string() }) }))
    .output(DeleteEvaluationExperimentResponseSchema),
  cancelEvaluationExperiment: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'POST',
      path: '/evaluation/experiments/{experimentId}/cancel',
      summary:
        'Cancel a running evaluation experiment and abort in-flight Neo work',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: z.object({ experimentId: z.string() }) }))
    .output(CancelEvaluationExperimentResponseSchema),
  rerunEvaluationExperiment: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'POST',
      path: '/evaluation/experiments/{experimentId}/rerun',
      summary: 'Rerun an experiment with the same dataset and configurations',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: z.object({ experimentId: z.string() }),
        body: RerunEvaluationExperimentRequestSchema,
      }),
    )
    .output(CreateEvaluationExperimentResponseSchema),
  setEvaluationRegressionSchedule: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'POST',
      path: '/evaluation/experiments/{experimentId}/regression-schedule',
      summary:
        'Enable or disable the scheduled regression suite for an experiment',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: z.object({ experimentId: z.string() }),
        body: SetEvaluationRegressionScheduleRequestSchema,
      }),
    )
    .output(SetEvaluationRegressionScheduleResponseSchema),
  retriggerFailedExperimentRuns: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'POST',
      path: '/evaluation/experiments/{experimentId}/retrigger-failed',
      summary: 'Retrigger all failed runs in an experiment',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: z.object({ experimentId: z.string() }),
        body: RetriggerFailedExperimentRunsRequestSchema,
      }),
    )
    .output(RetriggerFailedExperimentRunsResponseSchema),
  listReusableExperimentConfigs: terraContract
    .meta({ permissions: ['Evaluation Read'] })
    .route({
      method: 'GET',
      path: '/evaluation/experiments/{experimentId}/reusable-configs',
      summary:
        'List completed configs from other experiments that match this experiment',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: z.object({ experimentId: z.string() }) }))
    .output(ListReusableExperimentConfigsResponseSchema),
  previewReusableExperimentConfigs: terraContract
    .meta({ permissions: ['Evaluation Read'] })
    .route({
      method: 'POST',
      path: '/evaluation/experiments/reusable-configs/preview',
      summary:
        'Preview completed configs from other experiments that match a planned experiment',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: PreviewReusableExperimentConfigsRequestSchema }))
    .output(PreviewReusableExperimentConfigsResponseSchema),
  reuseExperimentConfigs: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'POST',
      path: '/evaluation/experiments/{experimentId}/reuse-configs',
      summary:
        'Replace selected configs with matching completed runs from other experiments',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: z.object({ experimentId: z.string() }),
        body: ReuseExperimentConfigsRequestSchema,
      }),
    )
    .output(ReuseExperimentConfigsResponseSchema),
  saveEvaluationConfigPreset: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'POST',
      path: '/evaluation/config-presets',
      summary: 'Save an evaluation configuration preset for reuse',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: SaveEvaluationConfigPresetRequestSchema }))
    .output(SaveEvaluationConfigPresetResponseSchema),
  listEvaluationConfigPresets: terraContract
    .meta({ permissions: ['Evaluation Read'] })
    .route({
      method: 'GET',
      path: '/evaluation/config-presets',
      summary: 'List saved evaluation configuration presets',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        query: z.object({
          componentId: z.string().optional(),
        }),
      }),
    )
    .output(ListEvaluationConfigPresetsResponseSchema),
  deleteEvaluationConfigPreset: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'DELETE',
      path: '/evaluation/config-presets/{presetId}',
      summary: 'Delete a saved evaluation configuration preset',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: z.object({ presetId: z.string() }) }))
    .output(DeleteEvaluationConfigPresetResponseSchema),
  listEvaluationDatasets: terraContract
    .meta({ permissions: ['Evaluation Read'] })
    .route({
      method: 'GET',
      path: '/evaluation/datasets',
      summary: 'List evaluation datasets',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        query: z.object({
          search: z.string().trim().max(200).optional(),
          datasetType: EvaluationDatasetTypeSchema.optional(),
          sortBy: z.enum(['name', 'createdAt']).optional(),
          sortOrder: z.enum(['asc', 'desc']).optional(),
          limit: z.coerce.number().min(1).max(500).optional().prefault(500),
          offset: z.coerce.number().min(0).optional().prefault(0),
          excludeGrouped: z.coerce.boolean().optional(),
        }),
      }),
    )
    .output(ListEvaluationDatasetsResponseSchema),
  importEvaluationDataset: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'POST',
      path: '/evaluation/datasets/import',
      summary: 'Import an evaluation dataset bundle',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: ImportEvaluationDatasetRequestSchema }))
    .output(ImportEvaluationDatasetResponseSchema),
  cloneEvaluationDataset: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'POST',
      path: '/evaluation/datasets/clone',
      summary:
        'Clone an evaluation dataset bundle and expectations for post-attack use',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: CloneEvaluationDatasetRequestSchema }))
    .output(CloneEvaluationDatasetResponseSchema),
  getEvaluationDatasetExpectations: terraContract
    .meta({ permissions: ['Evaluation Read'] })
    .route({
      method: 'GET',
      path: '/evaluation/datasets/{datasetId}/expectations',
      summary: 'Read per-sample expected outcomes for a dataset',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: z.object({ datasetId: z.string() }),
        query: z.object({
          componentId: z.string().min(1).optional(),
          remapToInstantiation: z.coerce.boolean().optional(),
        }),
      }),
    )
    .output(EvaluationDatasetExpectationsResponseSchema),
  patchEvaluationDatasetExpectations: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'PATCH',
      path: '/evaluation/datasets/{datasetId}/expectations',
      summary: 'Set per-sample expected outcomes for a dataset',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: z.object({ datasetId: z.string() }),
        body: PatchEvaluationDatasetExpectationsRequestSchema,
      }),
    )
    .output(EvaluationDatasetExpectationsResponseSchema),
  deleteEvaluationDataset: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'DELETE',
      path: '/evaluation/datasets/{datasetId}',
      summary: 'Delete an evaluation dataset',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: z.object({ datasetId: z.string() }) }))
    .output(DeleteEvaluationDatasetResponseSchema),
  bulkDeleteEvaluationDatasets: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'POST',
      path: '/evaluation/datasets/bulk-delete',
      summary: 'Delete multiple evaluation datasets',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: BulkDeleteEvaluationDatasetsRequestSchema }))
    .output(BulkDeleteEvaluationDatasetsResponseSchema),
  listEvalTenantApplications: terraContract
    .meta({ permissions: ['Evaluation Read'] })
    .route({
      method: 'GET',
      path: '/evaluation/eval-tenant/applications',
      summary: 'List instantiated applications in the eval tenant',
      inputStructure: 'detailed',
    })
    .output(ListEvalTenantApplicationsResponseSchema),
  deleteEvalTenantApplication: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'DELETE',
      path: '/evaluation/eval-tenant/applications/{applicationId}',
      summary: 'Delete an instantiated eval-tenant application',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: z.object({ applicationId: z.string() }),
        body: DeleteEvalTenantApplicationRequestSchema.optional(),
      }),
    )
    .output(DeleteEvalTenantApplicationResponseSchema),
  disableEvalTenantApplicationAutomation: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'POST',
      path: '/evaluation/eval-tenant/applications/{applicationId}/disable-automation',
      summary: 'Disable automation on an instantiated eval-tenant application',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: z.object({ applicationId: z.string() }) }))
    .output(DisableEvalTenantApplicationAutomationResponseSchema),
  deleteEvaluationDatasetTasks: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'DELETE',
      path: '/evaluation/datasets/{datasetId}/tasks',
      summary: 'Remove tasks (signals) from an evaluation dataset',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: z.object({ datasetId: z.string() }),
        body: DeleteEvaluationDatasetTasksRequestSchema,
      }),
    )
    .output(DeleteEvaluationDatasetTasksResponseSchema),
  reorderEvaluationDatasetTasks: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'PATCH',
      path: '/evaluation/datasets/{datasetId}/tasks/order',
      summary: 'Reorder tasks (signals) in an evaluation dataset',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: z.object({ datasetId: z.string() }),
        body: ReorderEvaluationDatasetTasksRequestSchema,
      }),
    )
    .output(ReorderEvaluationDatasetTasksResponseSchema),
  listEvaluationDatasetRoots: terraContract
    .meta({ permissions: ['Evaluation Read'] })
    .route({
      method: 'GET',
      path: '/evaluation/datasets/roots',
      summary: 'List the entities a dataset snapshot can be captured from',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        query: z.object({
          rootType: EvaluationDatasetRootTypeSchema,
          tenantId: z.string().min(1).optional(),
        }),
      }),
    )
    .output(ListEvaluationDatasetRootsResponseSchema),
  snapshotEvaluationDataset: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'POST',
      path: '/evaluation/datasets/snapshot',
      summary: 'Snapshot a tenant application into an evaluation dataset',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: SnapshotEvaluationDatasetRequestSchema }))
    .output(SnapshotEvaluationDatasetResponseSchema),
  instantiateEvaluationDataset: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'POST',
      path: '/evaluation/datasets/instantiate',
      summary: 'Instantiate a dataset into an evaluation tenant',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: InstantiateEvaluationDatasetRequestSchema }))
    .output(InstantiateEvaluationDatasetResponseSchema),
  ensureEvalTenantDatasetInstantiation: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'POST',
      path: '/evaluation/datasets/ensure-eval-tenant-instantiation',
      summary: 'Ensure a dataset is instantiated into the shared eval tenant',
      inputStructure: 'detailed',
    })
    .input(
      z.object({ body: EnsureEvalTenantDatasetInstantiationRequestSchema }),
    )
    .output(EnsureEvalTenantDatasetInstantiationResponseSchema),
  listEvaluationDatasetInstantiations: terraContract
    .meta({ permissions: ['Evaluation Read'] })
    .route({
      method: 'GET',
      path: '/evaluation/datasets/instantiations',
      summary: 'List dataset instantiations',
      inputStructure: 'detailed',
    })
    .input(z.object({ query: ListEvaluationDatasetInstantiationsQuerySchema }))
    .output(ListEvaluationDatasetInstantiationsResponseSchema),
  listEvaluationComponents: terraContract
    .meta({ permissions: ['Evaluation Read'] })
    .route({
      method: 'GET',
      path: '/evaluation/components',
      summary: 'List evaluable components with declared I/O schemas',
      inputStructure: 'detailed',
    })
    .output(ListEvaluationComponentsResponseSchema),
  getEvaluationComponentTrends: terraContract
    .meta({ permissions: ['Evaluation Read'] })
    .route({
      method: 'GET',
      path: '/evaluation/components/trends',
      summary: 'Get score and pass-rate trends for an evaluable component',
      inputStructure: 'detailed',
    })
    .input(z.object({ query: EvaluationComponentTrendsQuerySchema }))
    .output(EvaluationComponentTrendsResponseSchema),
  invokeEvaluationComponent: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'POST',
      path: '/evaluation/components/{componentId}/invoke',
      summary: 'Directly invoke a registered evaluable component',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: InvokeEvaluationComponentParamsSchema,
        body: InvokeEvaluationComponentBodySchema,
      }),
    )
    .output(InvokeEvaluationComponentResponseSchema),
  getEvaluationPostAttackPlaybook: terraContract
    .meta({ permissions: ['Evaluation Read'] })
    .route({
      method: 'GET',
      path: '/evaluation/playbooks/post-attack',
      summary: 'Get the canonical on-disk post-attack playbook for a category',
      inputStructure: 'detailed',
    })
    .input(z.object({ query: GetEvaluationPostAttackPlaybookQuerySchema }))
    .output(GetEvaluationPostAttackPlaybookResponseSchema),
  suggestEvaluationData: terraContract
    .meta({ permissions: ['Evaluation Read'] })
    .route({
      method: 'POST',
      path: '/evaluation/data/suggest',
      summary: 'Suggest concrete data matching an agent manifest',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: SuggestEvaluationDataRequestSchema }))
    .output(SuggestEvaluationDataResponseSchema),
  suggestEvaluationDatasetExpectations: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'POST',
      path: '/evaluation/datasets/suggest-expectations',
      summary:
        'Suggest ground-truth attack labels for signals from a vulnerability catalog',
      inputStructure: 'detailed',
    })
    .input(
      z.object({ body: SuggestEvaluationDatasetExpectationsRequestSchema }),
    )
    .output(SuggestEvaluationDatasetExpectationsResponseSchema),
  listEvaluationDatasetGroups: terraContract
    .meta({ permissions: ['Evaluation Read'] })
    .route({
      method: 'GET',
      path: '/evaluation/dataset-groups',
      summary: 'List evaluation dataset groups',
      inputStructure: 'detailed',
    })
    .output(ListEvaluationDatasetGroupsResponseSchema),
  getEvaluationDatasetGroup: terraContract
    .meta({ permissions: ['Evaluation Read'] })
    .route({
      method: 'GET',
      path: '/evaluation/dataset-groups/{groupId}',
      summary: 'Get an evaluation dataset group',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: z.object({ groupId: z.string() }) }))
    .output(GetEvaluationDatasetGroupResponseSchema),
  createEvaluationDatasetGroup: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'POST',
      path: '/evaluation/dataset-groups',
      summary: 'Create a dataset group from existing datasets',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: CreateEvaluationDatasetGroupRequestSchema }))
    .output(CreateEvaluationDatasetGroupResponseSchema),
  createEvaluationDatasetGroupFromFalsePositives: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'POST',
      path: '/evaluation/dataset-groups/from-false-positives',
      summary:
        'Create per-tenant datasets from false-positive signals and wrap them in a group',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        body: CreateEvaluationDatasetGroupFromFalsePositivesRequestSchema,
      }),
    )
    .output(CreateEvaluationDatasetGroupFromFalsePositivesResponseSchema),
  updateEvaluationDatasetGroupMembers: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'PATCH',
      path: '/evaluation/dataset-groups/{groupId}/members',
      summary: 'Replace the member datasets of a dataset group',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: z.object({ groupId: z.string() }),
        body: UpdateEvaluationDatasetGroupMembersRequestSchema,
      }),
    )
    .output(UpdateEvaluationDatasetGroupMembersResponseSchema),
  deleteEvaluationDatasetGroup: terraContract
    .meta({ permissions: ['Evaluation Execute'] })
    .route({
      method: 'DELETE',
      path: '/evaluation/dataset-groups/{groupId}',
      summary:
        'Delete a dataset group pointer, optionally deleting member datasets',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: z.object({ groupId: z.string() }),
        body: DeleteEvaluationDatasetGroupRequestSchema.optional(),
      }),
    )
    .output(DeleteEvaluationDatasetGroupResponseSchema),
};
